import {
  IGetUsersDisplayDataRequest,
  IMarkAsUnknownRequest,
  IUserDisplayData,
  UserModelErrorCode,
} from '@gem/models/userModel';
import { logger } from '@gem/modules/logger';
import { clearPhoneNumber } from '@gem/utils';

import { IUser } from 'APP/model/auth/authModel.types';
import { EmptyResponseError } from 'APP/model/error/errors/EmptyResponseError';
import { WsRequestError } from 'APP/model/error/errors/WsRequestError';
import { IUpdateUserDisplayDataB2B } from 'APP/model/userSettings/userSettingsModel.types';
import {
  getUsersByEmailsRequest,
  getUsersByIdsRequest,
  updateProfileRequest,
} from 'APP/wsApi/wsAppApi/requests/userRequests';

import { buildModelError } from '../error/errorBuilder';
import {
  mapGetUsersByEmailRequest,
  mapIUser,
  mapPersonalizedUserResponse,
} from './userModel.b2b.mapper';
import { IGetUsersByEmailsParams } from './userModel.types';

// Needs Bff implementation
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// eslint-disable-next-line
export const markAsUnknown = async (data: IMarkAsUnknownRequest): Promise<void> => {
  try {
    return;
  } catch (error) {
    logger.get('API').error('user.markAsUnknown', error);
    throw buildModelError(error);
  }
};

export async function getUsersDisplayData(
  data: IGetUsersDisplayDataRequest
): Promise<IUserDisplayData[]> {
  try {
    const response = await getUsersByIdsRequest({
      userIds: data.userIds.map((userId) => BigInt(userId)),
    });

    return response.type.value?.items.map(mapPersonalizedUserResponse) || [];
  } catch (error) {
    logger.get('API').error('getUsersDisplayData', error);
    throw buildModelError(error);
  }
}

export async function getUsersByEmails(data: IGetUsersByEmailsParams): Promise<IUserDisplayData[]> {
  try {
    const request = mapGetUsersByEmailRequest(data);
    const result = await getUsersByEmailsRequest(request);

    if (result.type.case === 'success') {
      return result.type.value.user.map((user) =>
        mapPersonalizedUserResponse({
          $typeName: 'com.gem.bff.contract.PersonalizedUser',
          user,
          lastOnlineTs: BigInt(0),
        })
      );
    } else {
      return [];
    }
  } catch (error) {
    logger.get('API').error('getUsersByEmails', error);
    throw buildModelError(error);
  }
}

export async function updateProfile(data: IUpdateUserDisplayDataB2B): Promise<IUser> {
  try {
    const response = await updateProfileRequest({
      nickname: data.nickName,
      avatarUrl: data.avatarUrl,
      phone: clearPhoneNumber(data.phone),
      position: data.position,
      firstName: data?.firstName,
      lastName: data?.lastName,
      middleName: data?.middleName,
      displayName: data.displayName,
    });

    switch (response.type.case) {
      case 'nicknameAlreadyExistError':
        throw new WsRequestError(UserModelErrorCode.NicknameAlreadyExistError, response);
      case 'usernameSymbolsNotValidError':
        throw new WsRequestError(UserModelErrorCode.UsernameSymbolsNotValidError, response);
      case 'usernameTooShortError':
        throw new WsRequestError(UserModelErrorCode.UsernameTooShortError, response);
      case 'usernameTooLongError':
        throw new WsRequestError(UserModelErrorCode.UsernameTooLongError, response);
      case 'nicknameTooShortError':
        throw new WsRequestError(UserModelErrorCode.NicknameTooShortError, response);
      case 'nicknameTooLongError':
        throw new WsRequestError(UserModelErrorCode.NicknameTooLongError, response);
    }

    if (!response.type.value?.user) {
      throw new EmptyResponseError('updateProfile', response);
    }

    return mapIUser(response.type.value.user);
  } catch (error) {
    logger.get('API').error('userSettings.updateUserProfile', error);
    throw buildModelError(error);
  }
}
